import { useCallback } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './design-system.css';
import { ThemeProvider } from './ThemeProvider';
import { ThemeId } from './themes';
import { ToastProvider } from './primitives';
import { CompareView } from './pages/CompareView';
import { DesignIndex } from './pages/DesignIndex';
import { ThemeDemoPage } from './pages/ThemeDemoPage';
import { ImprovementBoard } from './pages/ImprovementBoard';

/**
 * Entry point for the design exploration.
 *
 * Mounted from `App.tsx` *outside* the production `AppShell`, so each demo
 * can show its own redesigned header and sidebar, and only for the
 * "Developers" clearance group. Everything under here is self-contained: no
 * API calls, no Redux, no production component imports except the shared
 * icon registry and two utilities.
 *
 * `design-system.css` is imported here, but App.tsx imports this module
 * statically, so the stylesheet is in the main bundle and applies to the
 * whole app. The real app depends on it: `src/tailwindcomponents/` render
 * `.ds-*` classes. Only the reset rules are `.ds-root`-scoped.
 *
 * Routes (HashRouter, so the address bar shows `#/design/...`):
 *   /design                     the five directions, with live miniatures
 *   /design/compare             all five side by side
 *   /design/improvements        the improvement board prototype
 *   /design/:themeId            that direction's full demo screen
 *   /design/:themeId/gallery    every primitive in that direction
 */
export const DesignRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Picking a direction in the settings panel updates the address bar, so a
   * link copied from a demo opens what you were actually looking at.
   *
   * Only fires on a deliberate theme choice, and only while a demo route is
   * open — the index and compare views show all five at once, so rewriting
   * their URL would be wrong.
   */
  const handleThemeChange = useCallback(
    (id: ThemeId) => {
      const onDemo = /^\/design\/[^/]+/.test(location.pathname);
      if (!onDemo) return;
      const suffix = location.pathname.endsWith('/gallery') ? '/gallery' : '';
      const next = `/design/${id}${suffix}`;
      if (location.pathname !== next) navigate(next, { replace: true });
    },
    [navigate, location.pathname],
  );

  return (
    <ThemeProvider onThemeChange={handleThemeChange}>
      <ToastProvider>
        <Routes>
          <Route index element={<DesignIndex />} />
          <Route path="compare" element={<CompareView />} />
          <Route path="improvements" element={<ImprovementBoard />} />
          <Route path=":themeId" element={<ThemeDemoPage />} />
          <Route path=":themeId/gallery" element={<ThemeDemoPage gallery />} />
          <Route path="*" element={<Navigate to="/design" replace />} />
        </Routes>
      </ToastProvider>
    </ThemeProvider>
  );
};

export default DesignRoutes;
